import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAllPrices, getAllSites, getAllYards, getPricing, getSourcing } from '../services/api';
import { useSessionState } from '../hooks/useSessionState';
import MaterialFilter from '../components/MaterialFilter';
import YardPricesGrid from '../components/YardPricesGrid';
import CheapestYardPanel from '../components/CheapestYardPanel';
import OrdersPanel from '../components/OrdersPanel';
import HomeMap, { type MapMode, type OrderRequest } from '../components/HomeMap';
import AIChat from '../components/AIChat';
import HomeTour from '../components/HomeTour';
import FirstVisitPrompt from '../components/FirstVisitPrompt';
import DeliveryOrderModal, { type DeliveryOrderPrefill } from '../components/DeliveryOrderModal';
import InfoTip from '../components/InfoTip';
import { TIPS } from '../tour/copy';

export default function HomePage() {
  const [material, setMaterial] = useSessionState<string>('home:material', 'hardwood_mulch');
  const [selectedYard, setSelectedYard] = useState<string | null>(null);
  const [siteId, setSiteId] = useState<string | null>(null);
  const [mode, setMode] = useState<MapMode>('idle');
  const [orderPrefill, setOrderPrefill] = useState<DeliveryOrderPrefill | null>(null);

  const yardsQ = useQuery({ queryKey: ['yards-all'], queryFn: getAllYards });
  const pricesQ = useQuery({ queryKey: ['prices-all'], queryFn: getAllPrices });
  const sitesQ = useQuery({ queryKey: ['sites-all'], queryFn: getAllSites });

  const pricingQ = useQuery({
    queryKey: ['pricing', selectedYard, material],
    queryFn: () => getPricing(selectedYard!, material),
    enabled: mode === 'selling' && !!selectedYard,
  });
  const sourcingQ = useQuery({
    queryKey: ['sourcing', siteId, material],
    queryFn: () => getSourcing(siteId!, material),
    enabled: !!siteId,
  });

  const yards = yardsQ.data?.yards ?? [];
  const prices = pricesQ.data?.prices ?? [];
  const sites = sitesQ.data?.sites ?? [];

  const selectedSite = useMemo(() => {
    const s = sites.find((x) => x.site_id === siteId);
    return s
      ? { lat: s.lat, lon: s.lon, name: s.name ?? undefined, address: s.address ?? undefined, site_id: s.site_id }
      : null;
  }, [sites, siteId]);

  const onSelectYard = (yardId: string) => {
    setSelectedYard(yardId);
    setSiteId(null);
    setMode('selling');
  };
  const onSelectSite = (id: string | null) => {
    setSiteId(id);
    setMode(id ? 'cheapest' : 'idle');
  };

  const openOrderFromRequest = (req: OrderRequest) => {
    setOrderPrefill({
      yardId: req.yardId,
      material,
      quantity: req.quantity,
      deliveredPerUnit: req.deliveredPerUnit,
      siteId: req.siteId,
      siteName: req.siteName,
      siteAddress: req.siteAddress,
    });
  };

  return (
    <div className="home-grid">
      {/* prices (top-left) */}
      <div style={{ gridArea: 'prices', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div
          data-tour="material-filter"
          style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}
        >
          <span style={{ fontWeight: 600, color: 'var(--text-2)', whiteSpace: 'nowrap' }}>
            Select Material →
          </span>
          <MaterialFilter value={material} onChange={(m) => setMaterial(m)} />
          {mode !== 'idle' && (
            <button
              className="btn btn-sm"
              onClick={() => {
                setMode('idle');
                setSelectedYard(null);
                setSiteId(null);
              }}
            >
              Reset map
            </button>
          )}
        </div>
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <YardPricesGrid material={material} selectedYardId={selectedYard} onSelectYard={onSelectYard} />
        </div>
      </div>

      {/* cheapest yard (top-right of left column) */}
      <div className="card home-module" data-tour="cheapest-panel" style={{ gridArea: 'cheapest' }}>
        <div className="home-module-header">
          <span>
            Cheapest Yard
            <InfoTip content={TIPS.cheapestPanel} />
          </span>
        </div>
        <div className="home-module-body">
          <CheapestYardPanel
            sites={sites}
            material={material}
            siteId={siteId}
            onSelectSite={onSelectSite}
            rows={sourcingQ.data?.rows ?? []}
            loading={sourcingQ.isFetching}
            selectedYardId={selectedYard}
            onHighlightYard={(id) => setSelectedYard(id)}
            onRequestOrder={openOrderFromRequest}
          />
        </div>
      </div>

      {/* orders (full bottom-left) */}
      <OrdersPanel />

      {/* map (full right) */}
      <div className="card home-map-cell" style={{ gridArea: 'map' }} data-tour="home-map">
        <HomeMap
          mode={mode}
          material={material}
          yards={yards}
          sites={sites}
          prices={prices}
          pricingRows={pricingQ.data?.rows ?? []}
          sourcingRows={sourcingQ.data?.rows ?? []}
          selectedSite={selectedSite}
          onRequestOrder={openOrderFromRequest}
          onSelectSite={onSelectSite}
        />
        <AIChat yards={yards} />
      </div>

      {orderPrefill && (
        <DeliveryOrderModal prefill={orderPrefill} yards={yards} onClose={() => setOrderPrefill(null)} />
      )}

      <HomeTour
        setMaterial={setMaterial}
        onSelectYard={onSelectYard}
        onSelectSite={onSelectSite}
        resetMap={() => {
          setMode('idle');
          setSelectedYard(null);
          setSiteId(null);
        }}
        firstYardId={yards[0]?.yard_id ?? null}
        firstSiteId={sites[0]?.site_id ?? null}
      />

      <FirstVisitPrompt />
    </div>
  );
}
